/**
 * Factory for publication sections
 * @author Brendan Magee
 */

function PublicationSectionFactory($translate, $filter, $http, $q, Edition) {
  const daysOfWeek = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];
  const date = new Date();
  const hoursOfDay = Array.from({ length: 24 }, (value, index) => index);
  const daysOfMonth = Array.from({ length: 28 }, (value, index) => index + 1);

  const availableHoursOfDay = hoursOfDay.map(hour => ({
    id: hour,
    name: $filter('date')(date.setHours(hour, 0, 0, 0), 'shortTime')
  }));

  const availableDaysOfWeek = daysOfWeek.map((day, index) => ({
    id: index + 1,
    name: $translate.instant(`WEEKDAY.${day.toUpperCase()}`)
  }));

  const availableDaysOfMonth = daysOfMonth.map(day => ({
    id: day,
    name: $filter('date')(date.setDate(day), 'd')
  }));

  const errorMessages = {
    missingRequired: $translate.instant('PUBLICATION_SECTIONS.MISSING_REQUIRED'),
    urlUnsafe: $translate.instant('PUBLICATION_SECTIONS.URL_UNSAFE'),
    conflictingFields: $translate.instant('PUBLICATION_SECTIONS.CONFLICTING_FIELDS')
  };

  return class PublicationSection {
    constructor(data = {}) {
      Object.assign(this, PublicationSection.defaultProperties, data);
    }

    validateData() {
      const validationTests = {
        missingRequired: !this.id || !this.name || !this.hoursOfDay.length,
        urlUnsafe: /[^A-Za-z0-9_-]/g.test(this.id),
        conflictingFields: this.daysOfWeek.length && this.daysOfMonth.length
      };

      const errorMessage = Object.keys(validationTests).reduce((acc, key) => {
        let error = acc;
        if (validationTests[key]) {
          error += `${errorMessages[key]} `;
        }
        return error;
      }, '');

      const section = this.name || $translate.instant('PUBLICATION_SECTIONS.THIS_SECTION').toLowerCase();

      return errorMessage ? `${$translate.instant('PUBLICATION_SECTIONS.DATA_INVALID', { section })}: ${errorMessage}`.trim() : null;
    }

    searchEdition(timeStamp, page = 0) {
      if (!this.publicationId) {
        return $q.reject(`${this.name || 'This section'} is missing a publication ID`);
      }

      if (!timeStamp) {
        return $q.reject('`searchEdition` requires a timestamp argument');
      }

      this.editionStories.loading = true;

      const params = {
        includeStories: true,
        includeStoriesPage: page,
        includeStoriesSize: this.editionStories.pageSize
      };

      return $http.get(`api/publications/${this.publicationId}/sections/${this.id}/editions/${timeStamp}`, { params })
        .then((response) => {
          const { data } = response;
          const nextPage = data.stories || [];

          if (page === 0) {
            this.editionStories.results = [];
          }

          this.editionStories.results = [...this.editionStories.results, ...nextPage];
          data.stories = this.editionStories.results;

          if (data.storyIds && Array.isArray(data.storyIds)) {
            data.moreAvailable = data.stories.length < data.storyIds.length;
            data.currentPage = page;
          }

          return new Edition(data);
        }, (response) => {
          const errorMessage = response.data && response.data.error
            ? response.data.error
            : 'Error getting edition by timestamp';

          return $q.reject(errorMessage);
        })
        .finally(() => {
          this.editionStories.loading = false;
        });
    }

    getEditions(params = { includeTrailingStubs: true }) {
      if (!this.publicationId) {
        return $q.reject(`${this.name} is missing a publication ID`);
      }

      return $http.get(`api/publications/${this.publicationId}/sections/${this.id}/editions`, { params })
        .then(response => response.data.map(edition => new Edition(edition)));
    }

    getStoriesDownloadUrl(timeStamp, type = 'text/plain') {
      return `api/publications/${this.publicationId}/sections/${this.id}/editions/${timeStamp}/download?type=${type}`;
    }

    updateDaysOfWeek(day = {}) {
      const index = this.daysOfWeek.findIndex(id => id === day.id);
      if (index < 0) {
        this.daysOfWeek.push(day.id);
      } else {
        this.daysOfWeek.splice(index, 1);
      }
      return this;
    }

    updateEditionRecurrence() {
      switch (this.recurrence) {
        case 'daysOfWeek': {
          this.daysOfMonth = [];
          break;
        }

        case 'daysOfMonth': {
          this.daysOfWeek = [];
          break;
        }

        default: {
          this.daysOfWeek = [];
          this.daysOfMonth = [];
          break;
        }
      }
    }

    get error() {
      return this.validateData();
    }

    get schedule() {
      const schedule = {};
      Object.keys(this).forEach((prop) => {
        if (Array.isArray(this[prop]) && this[prop].length) {
          schedule[prop] = PublicationSection.schedule[prop].reduce((acc, item) => {
            if (this[prop].indexOf(item.id) > -1) {
              acc.push(item.name);
            }
            return acc;
          }, []).join(', ');
        }
      });
      return schedule;
    }

    get payload() {
      const { recurrence, expanded, editionStories, ...payload } = { ...this };
      Object.keys(payload).forEach((prop) => {
        if (Array.isArray(this[prop]) && !this[prop].length) {
          payload[prop] = null;
        } else if (Array.isArray(this[prop]) && this[prop].length > 1) {
          payload[prop] = this[prop].sort((first, second) => {
            if (first > second) {
              return 1;
            } else if (first < second) {
              return -1;
            }
            return 0;
          });
        }
      });
      return payload;
    }

    static get schedule() {
      return {
        hoursOfDay: availableHoursOfDay,
        daysOfWeek: availableDaysOfWeek,
        daysOfMonth: availableDaysOfMonth
      };
    }

    static get defaultProperties() {
      return {
        id: '',
        name: '',
        hoursOfDay: [],
        daysOfWeek: [],
        daysOfMonth: [],
        recurrence: 'daily',
        expanded: true,
        editionStories: {
          pageSize: 20,
          loading: false,
          results: []
        }
      };
    }
  };
}

const factoryArray = [
  '$translate',
  '$filter',
  '$http',
  '$q',
  'Edition',
  PublicationSectionFactory
];

export default function (ngModule) {
  ngModule.factory('PublicationSection', factoryArray);
}
